import { Button, Callout, Checkbox, Classes, FormGroup, Intent, Popover, Position, Switch } from '@blueprintjs/core';
import { MAX_REPORT_ITEMS, ReportItemType } from '@retro-ui/shared';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';

import { SciFinderAuthContext } from '@f/context';
import { createReport, getReportItemsNumber } from '@f/services/report/report.actions';
import { isDesigningModeEnabled } from '@f/services/report/report.selectors';
import { localStorageClient } from '@f/services/storage/wrapper';
import { pluralize } from '@f/utils/pluralize';
import { apiClient } from '@f/api-client';

import cls from './ReportTreePopover.module.css';

const ADD_TO_REPORT_ON_INIT_KEY = 'add_to_report_on_init';

class ReportTreePopover extends PureComponent {
  static contextType = SciFinderAuthContext;

  static propTypes = {
    canCreateReport: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    popoverProps: PropTypes.object,
    isReportDesigningMode: PropTypes.bool,
    designingReportId: PropTypes.string,
    createReport: PropTypes.func,
    onAddToReport: PropTypes.func,
    selectedResults: PropTypes.array,
    searchName: PropTypes.string,
    shouldEnableEditingMode: PropTypes.bool,
  };

  state = {
    elements: {
      [ReportItemType.SYNTHESIS_TREE]: true,
      [ReportItemType.REACTION]: false,
    },
    reportItemsCount: 0,
    itemsToAddCount: 0,
    isOpen: false,
    includeScifinderRelatedData: true,
  };

  componentDidUpdate() {
    const addToReportData = localStorageClient.getItem(ADD_TO_REPORT_ON_INIT_KEY);
    const { isAuthenticating } = this.context;

    if (addToReportData && !isAuthenticating) {
      const { elements, includeScifinderRelatedData, itemsToAddCount } = JSON.parse(addToReportData);
      this.setState({
        elements,
        includeScifinderRelatedData,
        itemsToAddCount,
        isOpen: true,
      });

      this.addElementsToReport(elements, includeScifinderRelatedData);
      localStorageClient.removeItem(ADD_TO_REPORT_ON_INIT_KEY);
    }
  }

  countReportItems = async (reportId, elements) => {
    const { selectedResults } = this.props;

    let reportItemsCount = 0;
    if (reportId) {
      reportItemsCount = await this.props.getReportItemsNumber(reportId);
    }

    const resultsWithSynthesis = selectedResults.filter(result => result.reactionCount != null);

    const { reactionsCount, synthesisCount } = resultsWithSynthesis.reduce(
      (accumulator, result) => {
        accumulator.reactionsCount += result.reactionCount;
        accumulator.synthesisCount += 1;
        return accumulator;
      },
      { reactionsCount: 0, synthesisCount: 0 }
    );

    let itemsToAddCount = 0;
    if (elements[ReportItemType.SYNTHESIS_TREE]) {
      itemsToAddCount += synthesisCount;
    }
    if (elements[ReportItemType.REACTION]) {
      itemsToAddCount += reactionsCount;
    }

    return { reportItemsCount, itemsToAddCount };
  };

  changeReportItemsCount = async elements => {
    const { designingReportId } = this.props;
    const reportId = designingReportId ?? null;

    const { reportItemsCount, itemsToAddCount } = await this.countReportItems(reportId, elements);

    this.setState({
      itemsToAddCount,
      reportItemsCount,
    });
  };

  changeElement = key => {
    const { elements } = this.state;

    const updatedElements = {
      ...elements,
      [key]: !elements[key],
    };

    this.setState({
      elements: updatedElements,
    });

    this.changeReportItemsCount(updatedElements);
  };

  onSynthesisTreeElementChange = () => {
    this.changeElement(ReportItemType.SYNTHESIS_TREE);
  };

  onReactionElementChange = () => {
    this.changeElement(ReportItemType.REACTION);
  };

  onIncludeReferenceReactionsChange = () => {
    this.setState(state => ({
      includeScifinderRelatedData: !state.includeScifinderRelatedData,
    }));
  };

  addElementsToReport = async (elements, includeScifinderRelatedData) => {
    const { isReportDesigningMode, designingReportId, searchName, shouldEnableEditingMode = true } = this.props;

    let reportId;
    if (isReportDesigningMode) {
      reportId = designingReportId;
    } else {
      reportId = await this.props.createReport({
        shouldEnableEditingMode,
        data: {
          title: searchName,
        },
      });
    }

    this.props.onAddToReport(reportId, elements, includeScifinderRelatedData);
  };

  onSubmit = async event => {
    event.preventDefault();
    const { elements, includeScifinderRelatedData, itemsToAddCount } = this.state;
    const { accessToken, authenticate } = this.context;

    this.setState({ isOpen: false });

    let hasToAuthenticate = false;
    if (includeScifinderRelatedData) {
      if (!accessToken) {
        hasToAuthenticate = true;
      } else {
        const { body } = await apiClient.report.validateScifinderToken({
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
          redirectUnauthorized: false,
        });
        hasToAuthenticate = !body.valid;
      }
    }

    if (hasToAuthenticate) {
      localStorageClient.setItem(
        ADD_TO_REPORT_ON_INIT_KEY,
        JSON.stringify({ elements, includeScifinderRelatedData, itemsToAddCount })
      );
      await authenticate();
      return;
    }

    this.addElementsToReport(elements, includeScifinderRelatedData);
  };

  onClickButton = async event => {
    event.preventDefault();
    const { elements } = this.state;

    this.changeReportItemsCount(elements);
  };

  handleInteraction(nextOpenState) {
    this.setState({ isOpen: nextOpenState });
  }

  render() {
    const { isReportDesigningMode, canCreateReport, disabled, loading, popoverProps, selectedResults } = this.props;
    const { elements, reportItemsCount, itemsToAddCount, includeScifinderRelatedData } = this.state;
    const { accessToken, isAuthenticating } = this.context;

    const selectedItemsCount = selectedResults.length;

    const predictedReportItemsCount = Number(reportItemsCount) + Number(itemsToAddCount);

    const blockOverextendingReport = predictedReportItemsCount > MAX_REPORT_ITEMS;

    const signedInToSciFinder = accessToken != null;

    if (!canCreateReport && !isReportDesigningMode) {
      return null;
    }

    return (
      <Popover
        position={Position.CENTER_RIGHT}
        disabled={disabled}
        {...popoverProps}
        popoverClassName={cx(Classes.POPOVER_CONTENT_SIZING, popoverProps?.popoverClassName)}
        isOpen={this.state.isOpen}
        onInteraction={state => this.handleInteraction(state)}
      >
        <Button
          intent={Intent.PRIMARY}
          text={isReportDesigningMode ? 'Add to report' : 'Create new report'}
          icon="document-open"
          data-cy="button-report-create-or-add"
          onClick={this.onClickButton}
          disabled={selectedItemsCount === 0}
        />
        <form className={cls.form} onSubmit={this.onSubmit}>
          <h5 className={Classes.HEADING}>Elements to add</h5>

          <FormGroup helperText="Determines whether the result synthesis tree should be added to the report.">
            <Switch
              large
              label="Synthesis tree"
              disabled={disabled}
              checked={elements[ReportItemType.SYNTHESIS_TREE]}
              onChange={this.onSynthesisTreeElementChange}
              data-cy="switch-report-add-synthesis-tree"
            />
          </FormGroup>

          <FormGroup helperText="Determines whether each reaction should be added to the report one by one.">
            <Switch
              large
              label="Reactions"
              disabled={disabled}
              checked={elements[ReportItemType.REACTION]}
              onChange={this.onReactionElementChange}
              data-cy="switch-report-add-reactions"
            />
          </FormGroup>
          <Callout intent={Intent.PRIMARY}>
            {isReportDesigningMode &&
              `You're about to add ${itemsToAddCount} 
              ${pluralize(itemsToAddCount, 'item', 'items')}
              to the currently edited report.`}
            {!isReportDesigningMode &&
              `You're about to create a new report containing ${itemsToAddCount} 
              ${pluralize(itemsToAddCount, 'item', 'items')}.`}
          </Callout>
          {itemsToAddCount > 0 && isReportDesigningMode && (
            <Callout intent={Intent.PRIMARY}>
              {`The report currently consists of ${reportItemsCount} ${pluralize(
                reportItemsCount,
                'item',
                'items'
              )}. After adding the selected 
                ${pluralize(itemsToAddCount, 'item', 'items')}, the total will be ${predictedReportItemsCount}. `}
            </Callout>
          )}

          {blockOverextendingReport && (
            <Callout intent={Intent.DANGER}>
              {`The report must not contain more than ${MAX_REPORT_ITEMS} elements! 
              Please choose a smaller number of items or create a new report to include the selected ${pluralize(
                itemsToAddCount,
                'element',
                'elements'
              )}.`}
            </Callout>
          )}
          <Checkbox
            className={cls.checkbox}
            checked={includeScifinderRelatedData}
            label="Include SciFinder reference reactions and starting material links"
            onChange={this.onIncludeReferenceReactionsChange}
            data-cy="checkbox-report-include-references"
          />
          {includeScifinderRelatedData && !signedInToSciFinder && (
            <Callout intent={Intent.WARNING}>You will be redirected to SciFinder sign in page.</Callout>
          )}
          <div>
            <Button
              large
              type="submit"
              disabled={disabled || blockOverextendingReport || itemsToAddCount === 0}
              loading={loading || isAuthenticating}
              intent={Intent.PRIMARY}
              text={isReportDesigningMode ? 'Add to report' : 'Create new report'}
              icon="document-open"
              data-cy="button-report-submit"
            />
          </div>
        </form>
      </Popover>
    );
  }
}

function mapStateToProps(state) {
  return {
    isReportDesigningMode: isDesigningModeEnabled(state),
    designingReportId: state.report.designingId,
  };
}

export default connect(mapStateToProps, { createReport, getReportItemsNumber })(ReportTreePopover);
